// =====================================================================
// usePendingCounts.jsx - Pending action counts pentru badges sidebar
// =====================================================================
// Bonus A (Day 4, 20-05-2026).
//
// Returneaza un obiect { conges, timesheets } cu numerele de pending
// demandes/entries necesitand acțiunea curentului user.
//
// - Conges:     leaves.listPending()        (necesita leaves.approve.all)
// - Timesheets: timesheetsValidation.listPending() (necesita timesheets.approve.group)
//
// Polling: refetch la 60s. Fail-silent (eroare = 0).
// Permission gates aplicate: daca user n-are perm, count = null (nu se afiseaza).
// =====================================================================

import { useState, useEffect } from 'react';
import {
  leaves as leavesApi,
  timesheetsValidation,
  errors as errorsApi,
  safetyAlerts,
  readyRdvDocs,
} from '../services/api.js';
import { pastille } from '../pages/commande-prete/rdvDocs.js';
import { droitsCommandesPretes } from '../pages/commande-prete/droits.js';
import { useAuth } from './useAuth.jsx';

const POLL_INTERVAL_MS = 60_000; // 60s

export function usePendingCounts() {
  const { user, can } = useAuth();
  const [counts, setCounts] = useState({
    conges: null, timesheets: null, errors: null, safety: null, commandesPretes: null,
  });

  const canApproveLeaves = can('leaves.approve.all');
  const canApproveTimesheets = can('timesheets.approve.group');
  // M-006 — Erreurs : badge = mes propres erreurs non-vues
  const canReadOwnErrors = can('errors.read.own');
  // M-010 — Sécurité : échéances légales, contrôles en retard, actions
  // correctives. Le service ne renvoie que ce qui concerne l'appelant.
  const canSeeSafety = can('safety.declare.own') || can('safety.read.all')
    || can('maintenance.execute.own') || can('maintenance.plan.all');
  // Commandes prêtes v3 B (16-10-2026) : rendez-vous pas encore vus + documents à imprimer.
  // Droit « Voir les commandes prêtes » (SPEC v3 D, 17/10), comme la tuile.
  // Pastille = rendez-vous pas encore vus + documents à imprimer : seulement pour qui peut les traiter (« Imprimer »).
  const canReadyOrders = droitsCommandesPretes(can).imprimer;

  useEffect(() => {
    if (!user) return;
    if (!canApproveLeaves && !canApproveTimesheets && !canReadOwnErrors
      && !canSeeSafety && !canReadyOrders) return;

    let cancelled = false;

    async function fetchAll() {
      const next = { conges: null, timesheets: null, errors: null, safety: null, commandesPretes: null };

      if (canApproveLeaves) {
        try {
          const list = await leavesApi.listPending();
          next.conges = Array.isArray(list) ? list.length : (list?.items?.length ?? 0);
        } catch (_e) {
          next.conges = 0;
        }
      }

      if (canApproveTimesheets) {
        try {
          const list = await timesheetsValidation.listPending();
          next.timesheets = Array.isArray(list) ? list.length : (list?.items?.length ?? 0);
        } catch (_e) {
          next.timesheets = 0;
        }
      }

      if (canReadOwnErrors) {
        try {
          const r = await errorsApi.myUnseenCount();
          next.errors = r?.count ?? 0;
        } catch (_e) {
          next.errors = 0;
        }
      }

      if (canSeeSafety) {
        try {
          const r = await safetyAlerts.mine();
          next.safety = r?.counts?.total ?? 0;
        } catch (_e) {
          next.safety = 0;
        }
      }

      if (canReadyOrders) {
        try {
          next.commandesPretes = pastille(await readyRdvDocs.compteurs());
        } catch (_e) {
          next.commandesPretes = 0;
        }
      }

      if (!cancelled) setCounts(next);
    }

    fetchAll();
    const id = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.id, canApproveLeaves, canApproveTimesheets, canReadOwnErrors,
    canSeeSafety, canReadyOrders]);

  return counts;
}
